/**
 * Phase 4A - does this deal fit this investor's buy box?
 *
 * The buy box is not a new concept: `investor_profiles` already carries the return
 * thresholds, cheque size, preferred archetypes and markets. This scores a deal against
 * them and, more importantly, says WHY - "IRR 6.92% against a 7.00% bar" rather than
 * "72%". A score with no reasons cannot be defended to a broker, and a broker who cannot
 * defend it will not use it.
 *
 * It reuses the same metric values the scorecard computes. A second scoring path that
 * derived its own metrics would be occurrence #9 of this build's dominant defect class -
 * one part of the app modelling a deal differently from another - so the caller passes
 * the numbers already on screen rather than this module recomputing anything.
 *
 * Three rules keep a match honest, each pinned by a test:
 *   - a NULL threshold is not a constraint, so it counts as neither a fit nor a miss
 *   - an EMPTY preferred list means "no constraint", not "matches nothing"
 *   - a MISSING metric is a MISS, never a pass. Silence must not read as success.
 */

export interface BuyBoxProfile {
  name: string;
  threshold_cap_rate?: number | null;
  threshold_cash_on_cash?: number | null;
  threshold_irr?: number | null;
  threshold_equity_multiple?: number | null;
  threshold_dscr?: number | null;
  target_equity_min?: number | null;
  target_equity_max?: number | null;
  preferred_archetypes?: string[] | null;
  preferred_markets?: string[] | null;
  is_1031_buyer?: boolean | null;
}

export interface MatchableDeal {
  id: string;
  label: string;
  cap_rate?: number;
  avg_cash_on_cash?: number;
  irr?: number;
  equity_multiple?: number;
  dscr?: number;
  total_cash_invested?: number;
  archetype?: string | null;
  market?: string | null;
}

export interface MatchLine {
  label: string;
  actual: number | string | null;
  required: number | string | null;
  reason: string;
}

export interface MatchResult {
  deal: MatchableDeal;
  fits: MatchLine[];
  misses: MatchLine[];
  total: number;
  /** Fraction of the constraints that actually applied. */
  score: number;
  verdict: 'strong' | 'partial' | 'poor';
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Formats a percent-ish metric for a sentence a person reads. */
const pct = (v: number) => `${v.toFixed(2)}%`;
const ratio = (v: number) => `${v.toFixed(2)}x`;
const money = (v: number) => `$${Math.round(v).toLocaleString()}`;

export const matchDealToProfile = (deal: MatchableDeal, profile: BuyBoxProfile): MatchResult => {
  const fits: MatchLine[] = [];
  const misses: MatchLine[] = [];

  /** A return threshold. Null bar = not a constraint, so it is skipped entirely. */
  const gate = (
    label: string,
    actual: number | undefined,
    required: number | null | undefined,
    fmt: (v: number) => string
  ) => {
    if (!isNum(required)) return;                       // not a constraint
    if (!isNum(actual)) {
      misses.push({
        label, actual: null, required,
        reason: `${label} is not yet computed for this deal, so it cannot be shown to clear ${fmt(required)}`,
      });
      return;
    }
    const line: MatchLine = {
      label, actual, required,
      reason: `${fmt(actual)} against a ${fmt(required)} minimum`,
    };
    (actual >= required ? fits : misses).push(line);
  };

  gate('Cap rate', deal.cap_rate, profile.threshold_cap_rate, pct);
  gate('Cash-on-cash', deal.avg_cash_on_cash, profile.threshold_cash_on_cash, pct);
  gate('IRR', deal.irr, profile.threshold_irr, pct);
  gate('Equity multiple', deal.equity_multiple, profile.threshold_equity_multiple, ratio);
  gate('DSCR', deal.dscr, profile.threshold_dscr, ratio);

  // Cheque size. Endpoints inclusive - an investor who says "$3M to $5M" means it.
  const min = profile.target_equity_min;
  const max = profile.target_equity_max;
  if (isNum(min) || isNum(max)) {
    const equity = deal.total_cash_invested;
    // A one-sided range is common - Vinod has a $3M floor and no ceiling - and
    // "$3,000,000 to any" reads like a bug rather than an open top end.
    const range =
      isNum(min) && isNum(max) ? `${money(min)} to ${money(max)}`
      : isNum(min) ? `${money(min)} or more`
      : `up to ${money(max as number)}`;
    if (!isNum(equity)) {
      misses.push({ label: 'Equity size', actual: null, required: range,
        reason: `Equity required is not yet computed, so it cannot be matched to ${range}` });
    } else {
      const inRange = (!isNum(min) || equity >= min) && (!isNum(max) || equity <= max);
      const line: MatchLine = {
        label: 'Equity size', actual: equity, required: range,
        reason: `${money(equity)} against a target of ${range}`,
      };
      (inRange ? fits : misses).push(line);
    }
  }

  /** A preference list. Empty or absent means no constraint at all. */
  const listGate = (label: string, value: string | null | undefined, list: string[] | null | undefined) => {
    if (!list || list.length === 0) return;             // not a constraint
    const allowed = list.join(', ');
    if (!value) {
      misses.push({ label, actual: null, required: allowed,
        reason: `Not set on this deal, so it cannot be matched against ${allowed}` });
      return;
    }
    const hit = list.some(x => x.toLowerCase() === value.toLowerCase());
    const line: MatchLine = { label, actual: value, required: allowed,
      reason: hit ? `${value} is on the preferred list` : `${value} is not among ${allowed}` };
    (hit ? fits : misses).push(line);
  };

  listGate('Asset type', deal.archetype, profile.preferred_archetypes);
  listGate('Market', deal.market, profile.preferred_markets);

  const total = fits.length + misses.length;
  const score = total === 0 ? 0 : fits.length / total;

  // Deliberately three states, matching the scorecard's own GO / REVIEW / NO GO shape
  // rather than inventing a fourth vocabulary for the same idea.
  const verdict: MatchResult['verdict'] =
    total > 0 && misses.length === 0 ? 'strong'
    : score >= 0.5 ? 'partial'
    : 'poor';

  return { deal, fits, misses, total, score, verdict };
};

/**
 * Best fit first. The sort is stable on ties - an unstable sort would reshuffle equally
 * ranked deals between renders, which reads as the list changing on its own.
 */
export const rankDealsForProfile = (deals: MatchableDeal[], profile: BuyBoxProfile): MatchResult[] =>
  deals
    .map((deal, i) => ({ m: matchDealToProfile(deal, profile), i }))
    .sort((a, b) => (b.m.score - a.m.score) || (a.i - b.i))
    .map(x => x.m);
